// src/Pages/Etusivu/Etusivu.jsx
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { PlusCircle, MinusCircle } from "lucide-react";
import AOS from "aos";
import { api } from "../../services/api";
import WebDesignBanner from "../../components/HeaderWithAd/HeaderWithAd";
import WhyBuukkeriSection from "../../components/WhyBuukkeriSection/WhyBuukkeriSection";
import LatestArtists from "../../components/LatestArtists/LatestArtists.jsx";

import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import BookingModal from "../../components/BookingModal/BookingModal";
import WelcomeModal from "../../components/WelcomeModal/WelcomeModal.jsx";
import ArtistCard from "../../components/Esiintyjakortti/Esiintyjakortti";
import CompanyCard from "../../components/Yrityskortti/Yrityskortti.jsx"; // jätetään 
// paikoilleen helppoa palautusta varten
// import AdCard from "../../components/Mainoskortti/Mainoskortti";
// import mainosKuva from "../../assets/mainoskortti.webp";
import BookingFlow from "../../components/Bookkausneuvo/Bookkausneuvo";
import MonthlyFeatured from "../../components/MonthlyFeatured/MonthlyFeatured";
import { motion, AnimatePresence } from "framer-motion";
import { slugParamToNames, namesToSlugList } from "../../utils/fiRegion";

// 🧩 Overlay-spinneri (pyörivä logo + blur-tausta)
import { PageOverlayLoading } from "../../components/Loading/Spinner.jsx";
// 🔧 VAIHDA TÄHÄN oma taustakuva (tai poista ja käytä pelkkää gradienttia)
import heroBg from "../../assets/backstage_dark.webp";
// Logoassetit suoraan ESM-importeilla (älä käytä require() Viten kanssa)
import logoLight from "../../assets/logo-buukkeri-dark.webp";
import logoDark from "../../assets/logo-buukkeri-dark.webp";

const BOOT_MIN_SHOW_MS = 900;    // kevyempi ensimaalauksen minimiaika
const BOOT_FAILSAFE_MS = 5000;   // nopeampi hätäkatkaisu
const HOME_BOOT_KEY    = "bbx:homeBootShown"; // overlay vain kerran / selain-välilehti

// Älä näytä overlayta devissä/localhostissa
const IS_DEV =
  (import.meta?.env?.DEV ?? (typeof process !== "undefined" && process.env?.NODE_ENV !== "production")) ||
  (typeof window !== "undefined" && window?.location?.hostname === "localhost");

// Suppressoi overlayn jos tultiin sivuston sisältä (esim. /blogi → /)
function hasInternalReferrer() {
  try {
    if (typeof document === "undefined") return false;
    const ref = document.referrer || "";
    if (!ref) return false; // tyhjä referrer = ensimmäinen tulo tai referrer blokattu
    const curOrigin = window.location.origin;
    const refOrigin = new URL(ref).origin;
    return refOrigin === curOrigin; // sama origin → sisäinen navigaatio
  } catch {
    return false;
  }
}

// ---------------------------------------------------------
// FEATURE-FLAG: näytetäänkö yritykset etusivulla?
// Palauta yritykset vaihtamalla arvoon true.
// ---------------------------------------------------------
const SHOW_COMPANIES = false;

// FEATURE-FLAG: näytetäänkö tervetuloa-modal?
const SHOW_WELCOME = false;

// ---------------- Consts ----------------
const SEEN_KEY = "bilebox:bookingModalSeen";
const RESULTS_ID = "tulokset";
const DATE_KEY = "buukkeri:selecteddate";

// Muista valitut maakunnat jos URL:ia ei ole
const REGIONS_KEY = "buukkeri:selectedregions";
const QUICK_REGIONS = ["Uusimaa", "Pirkanmaa", "Varsinais-Suomi", "Päijät-Häme"];

const baseArtistTypes = SHOW_COMPANIES
  ? ["Bilebändi", "Duo", "Trubaduuri", "Sooloartisti", "DJ", "Yritys"]
  : ["Bilebändi", "Duo", "Trubaduuri", "Sooloartisti", "DJ"];

// URL-parametri → valikon arvo -kartta
const TYPE_MAP = {
  dj: "DJ",
  trubaduuri: "Trubaduuri",
  bilebandi: "Bilebändi",
  "bilebändi": "Bilebändi",
  sooloartisti: "Sooloartisti",
  duo: "Duo",
  all: ""
};

// UI-arvo → URL-paramin arvo
const TYPE_TO_PARAM = {
  DJ: "dj",
  Trubaduuri: "trubaduuri",
  "Bilebändi": "bilebandi",
  Sooloartisti: "sooloartisti",
  Duo: "duo",
  Yritys: "yritys",
};

// Pikavalinnat genreille (täsmälleen sama kirjoitusasu kuin public.genres.name)
const QUICK_GENRES = [
  "Iskelmä",
  "Bilemusiikki",
  "80-luvun listahitit",
  "Ulkomainen rock",
];

// Palauta tämän päivän pvm paikallisajassa muodossa YYYY-MM-DD
function todayLocalISO() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// --- listan ja itemien animaatiot (Framer Motion) ---
const listVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.02 },
  },
};
const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.28, ease: "easeOut" } },
  exit:   { opacity: 0, y: -8, transition: { duration: 0.18, ease: "easeIn" } },
};

const AnimatedLine = ({ children, type = "fade", delay = 0 }) => {
  const props =
    type === "zoom"
      ? {
          initial: { opacity: 0, scale: 0.96 },
          animate: { opacity: 1, scale: 1 },
          transition: { duration: 0.5, ease: "easeOut", delay },
        }
      : {
          initial: { opacity: 0, y: 6 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.55, ease: "easeOut", delay },
        };
  return (
    <motion.span {...props} className="block">
      {children}
    </motion.span>
  );
};

const formatDateFi = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${Number(d)}.${Number(m)}.${y}`;
};

// --- safe text helpers ---
const textOf = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.map(textOf).join(" ");
  if (typeof v === "object") {
    const known = ["label", "name", "region", "url"].map(k => v[k]).filter(Boolean);
    return (known.length ? known : Object.values(v)).map(textOf).join(" ");
  }
  return String(v);
};
const lower = (v) => textOf(v).toLowerCase();

// --- normalisoi genret palvelimen vastauksesta {items:[{id,name}]} tai suoraan taulukosta
function normalizeGenreNames(raw) {
  const list = Array.isArray(raw?.items) ? raw.items : (Array.isArray(raw) ? raw : []);
  const names = list
    .map(g => typeof g === "string" ? g : (g?.name ?? g?.label ?? g?.title ?? ""))
    .map(s => String(s).trim())
    .filter(Boolean);

  // dedupe case-insensitiivisesti, säilytä 1. kirjoitusasu
  const seen = new Set();
  const uniq = [];
  for (const n of names) {
    const key = n.toLowerCase();
    if (!seen.has(key)) { seen.add(key); uniq.push(n); }
  }

  // aakkosjärjestys (fi)
  uniq.sort((a, b) => a.localeCompare(b, "fi", { sensitivity: "base" }));
  return uniq;
}

// --- apuri: vertaa taulukoita järjestyksestä riippumatta ---
const eqArrIgnoreOrder = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const A = [...a].map((s) => String(s).toLowerCase()).sort();
  const B = [...b].map((s) => String(s).toLowerCase()).sort();
  return A.every((v, i) => v === B[i]);
};

// Palauttaa true = auki, false = kiinni, null = ei tietoa (ei kenttiä palautteessa)
function isOpenOnDay(entity, iso) {
  if (!iso || entity?.__kind === "company") return true; // yrityksille ei käytetä kalenteria

  // Mahdolliset kenttämuodot backendistä:
  const map =
    entity?.availability ||
    entity?.availability_map ||
    entity?.open_days_map ||
    null;

  if (map && typeof map === "object") {
    const v = map[iso];
    return v === true || v === 1 || String(v).toLowerCase() === "open";
  }

  const arr =
    entity?.open_dates ||
    entity?.open_days ||
    entity?.free_dates ||
    null;

  if (Array.isArray(arr)) {
    return arr.some((d) => String(d).slice(0, 10) === iso);
  }

  // ei yhtään tuntemaamme kenttää mukana
  return null;
}

export default function BandList() {
  const navigate = useNavigate();
  const rrLocation = useLocation();

  // --- SEO: otsikko/kuvaus + canonical ---
  const seo = useMemo(() => {
    const title = "Esiintyjät tapahtumaasi – helposti ja nopeasti | Buukkeri";
    const description =
      "Löydä täydellinen esiintyjä juhliisi – trubaduurit, bilebändit, duot ja DJ:t, kaikki samasta paikasta.";
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://buukkeri.fi";
    const canonical = `${origin}/`;
    return { title, description, canonical };
  }, []);

  // Ilmoita headerille sivun otsikko (mittausta varten)
  useEffect(() => {
    const path = `${rrLocation.pathname}${rrLocation.search || ""}`;
    window.dispatchEvent(
      new CustomEvent("bbx:title", { detail: { title: seo.title, path } })
    );
 }, [seo.title, rrLocation.pathname, rrLocation.search]);

  // ---------- Tilat ----------
  const [bands, setBands] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [availableArtistTypes, setAvailableArtistTypes] = useState([]);
  // Ei oletusta: luetaan vain jos URLissa on ?type=...
  const [selectedArtistTypes, setSelectedArtistTypes] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const raw = (p.get("type") || "").toLowerCase();
      const mapped = TYPE_MAP[raw];
      if (mapped === "") return [];       // all → Kaikki
      if (mapped) return [mapped];        // dj/trubaduuri/bilebandi
    } catch {}
    return [];                            // ei paramia → ei esivalintaa
  });

  const [availableGenres, setAvailableGenres] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);

  // Palvelut (yrityksille) – pidetään tila olemassa, mutta ei käytetä kun SHOW_COMPANIES=false
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  const [availableRegions, setAvailableRegions] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);

  // Estetään ensimmäisen renderin "räpsyntä" kunnes URL → state on tehty
  const [initialSyncDone, setInitialSyncDone] = useState(false);

  const PAGE_SIZE = 8;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Alusta pvm synkronisesti URL -> localStorage -> tänään,
  // jotta "tämän päivän" oletus EI ehdi yliajaa tallennettua arvoa.
  const [selectedDate, setSelectedDate] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        const p = new URLSearchParams(window.location.search);
        const dFromUrl = p.get("date");
        if (dFromUrl && /^\d{4}-\d{2}-\d{2}$/.test(dFromUrl)) return dFromUrl;
      }
      const saved = typeof localStorage !== "undefined" && localStorage.getItem(DATE_KEY);
      if (saved && /^\d{4}-\d{2}-\d{2}$/.test(saved)) return saved;
    } catch {}
    return todayLocalISO();
  });

  const [showBooking, setShowBooking] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [scrolledOnce, setScrolledOnce] = useState(false);

  // 🟣 BOOT-OVERLAY: neljän haun valmius
  const [genresReady, setGenresReady]   = useState(false);
  const [typesReady, setTypesReady]     = useState(false);
  const [listReady, setListReady]       = useState(false);
  const [regionsReady, setRegionsReady] = useState(false);
  const [bootLoading, setBootLoading]   = useState(() => {
    try {
      // 0) devissä/localhostissa ei overlayta
      if (IS_DEV) return false;
      // 1) jos jo näytetty tässä välilehdessä → ei overlayta
      if (sessionStorage.getItem(HOME_BOOT_KEY) === "1") return false;
      // 2) reload/back_forward → ei overlayta
      let navType = "navigate";
      try {
        const nav = performance.getEntriesByType?.("navigation")?.[0];
        navType =
          nav?.type ||
          (performance.navigation &&
            ["navigate", "reload", "back_forward", "prerender"][performance.navigation.type]);
      } catch {}
      if (navType === "reload" || navType === "back_forward") return false;
      // 3) sisäinen referrer → ei overlayta
      if (hasInternalReferrer && hasInternalReferrer()) return false;
      // 4) muuten näytä overlay kerran
      sessionStorage.setItem(HOME_BOOT_KEY, "1");
      return true;
    } catch { return false; }
  });
  // Ajastusrefit
  const bootStartRef = useRef(null);
  const bootHideTimerRef = useRef(null);
  const bootFailTimerRef = useRef(null);
  // Käynnistä hätäkatkaisu vain jos overlay oikeasti näytetään
  useEffect(() => {
    if (!bootLoading) return;
    bootStartRef.current = performance.now();
    try { sessionStorage.setItem(HOME_BOOT_KEY, "1"); } catch {}
    bootFailTimerRef.current = setTimeout(() => setBootLoading(false), BOOT_FAILSAFE_MS);
    return () => {
      clearTimeout(bootFailTimerRef.current);
      clearTimeout(bootHideTimerRef.current);
    };
  }, [bootLoading]);

// Poista bbx-booting heti kun overlay ei ole käytössä
// (tai overlay on ehtinyt jo sulkeutua). Overlay itse huolehtii
// bbx-overlay-open -luokasta.
useEffect(() => {
  try {
    if (!bootLoading) {
      document.documentElement.classList.remove("bbx-booting");
    }
  } catch {}
}, [bootLoading]);

// (poistettu duplikaatti-efekti)

  // Piilota overlay vasta, kun kaikki kyselyt valmiit JA miniminäyttöaika on kulunut
  useEffect(() => {
    if (!bootLoading) return;
    if (!(genresReady && typesReady && listReady && regionsReady)) return;
    const started = bootStartRef.current ?? performance.now();
    const elapsed = performance.now() - started;
    const wait = Math.max(0, BOOT_MIN_SHOW_MS - elapsed);
    clearTimeout(bootHideTimerRef.current);
    bootHideTimerRef.current = setTimeout(() => setBootLoading(false), wait);
  }, [genresReady, typesReady, listReady, regionsReady, bootLoading]);

  // ---------- Helpers ----------
  const closeBooking = useCallback(() => {
    setShowBooking(false);
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
    } catch {}
  }, []);

const normalizeArtist = (b) => {
  const o = {
    __kind: "artist",
    // säilytä KAIKKI alkuperäiset kentät (mm. instruments*, additional_services* jne.)
    ...b,
    // normalisoi vain nämä kaksi numeerista
    average_rating: Number(b?.average_rating ?? b?.rating?.average ?? 0),
    reviews_count: Number(b?.reviews_count ?? b?.total_reviews ?? b?.rating?.total ?? 0),
  };

  // Älä riko alkuperäistä genres/regions -dataa: lisää vain fallbackit jos puuttuu
  if (!o.genres || (Array.isArray(o.genres) && o.genres.length === 0)) {
    if (typeof b?.genre_string === "string") {
      o.genres = b.genre_string.split(",").map((s) => s.trim()).filter(Boolean);
    }
  }
  if (!o.regions) {
    o.regions = b?.performance_regions ?? [];
  }

  return o;
};

  const normalizeCompany = (c) => ({
    __kind: "company",
    id: c.id,
    slug: c.slug,
    name: c.name,
    profile_image: c.profile_image,
    location: c.location,
    regions: c.regions ?? c.service_regions ?? [],
    genres: [],
    artist_type: "Yritys",
    first_video_url: c.first_video_url,
    video_link: c.video_link,
    videos: c.videos,
    average_rating: Number(c.average_rating ?? 0),
    reviews_count: Number(c.reviews_count ?? 0),
    services: Array.isArray(c.services) ? c.services : [],
  });

  // Taulukko/objekti/string → alueiden nimet
  const extractRegionNames = (band = {}) => {
    const pull = (arr) =>
      Array.isArray(arr)
        ? arr
            .map((x) => (typeof x === "string" ? x : x?.name || x?.region || null))
            .filter(Boolean)
        : [];

    const names = [...pull(band.regions), ...pull(band.performance_regions)];

    if (typeof band.performance_regions_string === "string") {
      names.push(
        ...band.performance_regions_string
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      );
    }

    return [...new Set(names.map((s) => s.trim()))];
  };

  const extractGenres = (band = {}) => {
    if (Array.isArray(band.genres)) return band.genres;
    if (typeof band.genre_string === "string")
      return band.genre_string.split(",").map((s) => s.trim());
    if (typeof band.genre === "string")
      return band.genre.split(",").map((s) => s.trim());
    return [];
  };

  // Debounce hook
  function useDebouncedValue(value, delay = 250) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
      const t = setTimeout(() => setDebounced(value), delay);
      return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
  }

  // --- Johdetut tilat: tyyppi → company/artist, ja onko tagit pois käytöstä
  const selectedType = (selectedArtistTypes[0] || "").toLowerCase();
  const isCompanyMode = SHOW_COMPANIES && selectedType === "yritys"; // yritystilaa ei voi aktivoida kun flag=false
  const tagsDisabled = !selectedType;



  // Kun tyypiksi vaihtuu Yritys ↔ Esiintyjä, tyhjennä vastakkainen filtteri
  useEffect(() => {
    setSelectedGenres([]);
    setSelectedServices([]);
  }, [isCompanyMode]);

  // Kun tyyppi tyhjenee → nollaa molemmat tagilistat varmasti
  useEffect(() => {
    if (!selectedType) {
      setSelectedGenres([]);
      setSelectedServices([]);
    }
  }, [selectedType]);

  // Jos flag on false ja jokin tapa yrittää lukita "Yritys" tyypin (esim. vanha URL-state),
  // poistetaan se siististi.
  useEffect(() => {
    if (!SHOW_COMPANIES) {
      const next = selectedArtistTypes.filter(
        (t) => String(t).toLowerCase() !== "yritys"
      );
      if (next.length !== selectedArtistTypes.length) {
        setSelectedArtistTypes(next);
      }
    }
  }, [selectedArtistTypes]);

  // ---------- Efektit: AOS (käynnistä vasta overlayn jälkeen) ----------
  useEffect(() => {
    if (bootLoading) return;
    AOS.init({
      duration: 700,
      easing: "ease-out",
      offset: 80,
      once: true,                 // ei turhaa "läsähtelyä"
      anchorPlacement: "top-bottom",
    });
  }, [bootLoading]);
 

  useEffect(() => {
    if (bootLoading) return;
    if (typeof AOS?.refreshHard === "function") AOS.refreshHard();
  }, [visibleCount, bootLoading]);

  // ---------- Efekti: header-event ----------
  useEffect(() => {
    const onOpen = () => {
      
      setShowBooking(true);
    };
    window.addEventListener("bilebox:openBooking", onOpen);
    return () => window.removeEventListener("bilebox:openBooking", onOpen);
  }, []);

// Synkkaa tyyppi myös, kun URL-parametrit muuttuvat SPA-navigaatiossa
useEffect(() => {
  const p = new URLSearchParams(rrLocation.search);
  const raw = (p.get("type") || "").toLowerCase();
  const mapped = TYPE_MAP[raw];

  setSelectedArtistTypes((prev) => {
    if (mapped === "") {
      // ?type=all → tyhjennä valinta vain jos ei jo tyhjä
      return prev.length ? [] : prev;
    }
    if (!mapped) {
      // tuntematon arvo → ei muutosta
      return prev;
    }
    // Aseta vain jos oikeasti muuttuu (vältetään turhat renderit)
    return (prev.length === 1 && prev[0] === mapped) ? prev : [mapped];
  });
}, [rrLocation.search]);

// --- URL → state: clear=1 nollaa kaikki filtterit (paitsi TYYPPI ja PVM) ---
useEffect(() => {
  const p = new URLSearchParams(rrLocation.search);
  if (p.get("clear") !== "1") return;

  // 1) Nollaa UI-filtterit
  setSearchTerm("");
  setLocationFilter("");
  setSelectedGenres([]);
  setSelectedServices([]);
  // Huom: ÄLÄ nollaa maakuntia täällä. clear=1 linkeillä halutaan usein säilyttää ?maakunta=...
  // (Jos tarvitset joskus "täysnollauksen", tee sille oma parami, esim. clearAll=1)

  // 2) Siivoa URL (ettei maakunta palaudu toisen efektin kautta)
  p.delete("clear");
  p.delete("maakunta");
  navigate(
    { pathname: rrLocation.pathname, search: `?${p.toString()}`, hash: rrLocation.hash },
    { replace: true }
  );
}, [rrLocation.search, rrLocation.pathname, rrLocation.hash, navigate]);

// --- URL → state: ?maakunta=uusimaa,pirkanmaa (jo olemassa yllä)
// --- Muista maakunnat localStorageen, jos URL:ssa EI ole ?maakunta ---
useEffect(() => {
  const p = new URLSearchParams(rrLocation.search);
  const hasUrl = !!(p.get("maakunta") || "");
  if (hasUrl) return; // URL voittaa aina
  try {
    const raw = localStorage.getItem(REGIONS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const arr = Array.isArray(parsed) ? parsed : [];
    const sanitized = availableRegions.length
      ? arr.filter((n) => availableRegions.includes(n))
      : arr;
    setSelectedRegions((prev) =>
      eqArrIgnoreOrder(prev, sanitized) ? prev : sanitized
    );
  } catch {/* ignore */}
}, [availableRegions, rrLocation.search]);

// --- state → localStorage: tallenna maakuntavalinnat ---
useEffect(() => {
  try {
    localStorage.setItem(REGIONS_KEY, JSON.stringify(selectedRegions));
  } catch {/* ignore */}
}, [selectedRegions]);

// --- state → URL: pidä ?date=YYYY-MM-DD synkassa ---
useEffect(() => {
  const p = new URLSearchParams(rrLocation.search);
  const current = p.get("date") || "";
  const desired =
    selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate) ? selectedDate : "";
  if (current === desired) return;
  if (desired) p.set("date", desired);
  else p.delete("date");
  navigate(
    { pathname: rrLocation.pathname, search: `?${p.toString()}`, hash: rrLocation.hash },
    { replace: true }
  );
}, [selectedDate, rrLocation.pathname, rrLocation.search, rrLocation.hash, navigate, initialSyncDone]);

// Merkitse initial-sync valmiiksi, kun URL → state on asettunut vähintään kerran
useEffect(() => {
  if (!initialSyncDone) {
    setInitialSyncDone(true);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedDate, selectedArtistTypes.length, selectedRegions.length]);

// --- URL → state: ?maakunta=uusimaa,pirkanmaa ---
useEffect(() => {
  if (!initialSyncDone) return; // älä synkkaa ennen kuin initial URL→state tehty
  const p = new URLSearchParams(rrLocation.search);
  const raw = p.get("maakunta") || "";
  if (!raw) return;
  // Slugit -> viralliset nimet
  const fromUrl = slugParamToNames(raw);
  // Jos palvelin palauttaa rajatun listan, pidä vain sallitut
  const target = availableRegions.length
    ? fromUrl.filter((n) => availableRegions.includes(n))
    : fromUrl;
  setSelectedRegions((prev) => (eqArrIgnoreOrder(prev, target) ? prev : target));
// availableRegions riippuvuudessa, jotta URL:sta luettu säilyy kun nimet on ladattu
}, [rrLocation.search, availableRegions, initialSyncDone]);

// --- state → URL: pidä ?maakunta= syncissä valintojen kanssa ---
useEffect(() => {
  const p = new URLSearchParams(rrLocation.search);
  const current = p.get("maakunta") || "";
  const desired = namesToSlugList(selectedRegions).join(",");

  if (desired === current) return;

  if (desired) {
    p.set("maakunta", desired);
  } else {
    p.delete("maakunta");
  }

  navigate(
    { pathname: rrLocation.pathname, search: `?${p.toString()}`, hash: rrLocation.hash },
    { replace: true }
  );
}, [selectedRegions, rrLocation.pathname, rrLocation.search, rrLocation.hash, navigate, initialSyncDone]);

// Synkkaa UI → URL (päivitä ?type= kun käyttäjä vaihtaa pudotusvalikkoa)
useEffect(() => {
  if (!initialSyncDone) return; // estä ensimmäinen kierros
  const desired = TYPE_TO_PARAM[selectedArtistTypes[0]] || "";
  const p = new URLSearchParams(rrLocation.search);
  const current = p.get("type") || "";

  // Ei muutosta → ei navigointia
  if (desired === "" && current === "") return;
  if (desired === "") {
    // Kaikki → poista parami
    if (!current) return;
    p.delete("type");
  } else {
    if (current === desired) return;
    p.set("type", desired);
  }
  navigate(
    { pathname: rrLocation.pathname, search: `?${p.toString()}` },
    { replace: true } // ei turhaa historia-spämmiä
  );
}, [selectedArtistTypes, rrLocation.pathname, rrLocation.search, navigate, initialSyncDone]);
  
  useEffect(() => {
    if (!initialSyncDone) return; // estä ensimmäinen kierros
  const p = new URLSearchParams(rrLocation.search);
  const d = p.get("date");
  if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    setSelectedDate(d);
  }
  // ei siivoa URL:ia; vain synkkaa lomakkeen
}, [rrLocation.search, initialSyncDone]);

  // --- TALLENNA valittu päivä localStorageen aina kun muuttuu ---
  useEffect(() => {
    try {
      if (selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
        localStorage.setItem(DATE_KEY, selectedDate);
      }
    } catch {}
  }, [selectedDate]);

  // ---------- Efekti: query/state → modal + scroll ----------
  useEffect(() => {
    const params = new URLSearchParams(rrLocation.search);
    const wantsBooking = params.get("book") === "1";
    const fromState = rrLocation.state?.openBooking;
    // scrollTo voi tulla ilman #-merkkiä → normalisoidaan
    const rawTarget = rrLocation.state?.scrollTo || params.get("scrollTo") || RESULTS_ID;
    const target = rawTarget.startsWith("#") ? rawTarget : `#${rawTarget}`;

    // Jos URL tai state pyytää avaamaan haun → avaa pikahakumodali,
    // piilota tervetulomodal ja scrollaa kohteeseen.
    if (wantsBooking || fromState) {
      setShowWelcome(false);
      setShowBooking(true);

      requestAnimationFrame(() => {
        document.querySelector(target)?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });

      // siivoa URL/state ettei jää looppaamaan
      params.delete("book");
      params.delete("scrollTo");
      navigate(
        { pathname: rrLocation.pathname, search: params.toString() },
        { replace: true, state: {} }
      );
      return;
    }

    // Näytä tervetulomodal vain kerran per sessio (jos feature päällä)
    if (SHOW_WELCOME) {
      const seen = sessionStorage.getItem(SEEN_KEY) === "1";
      if (!seen && !showBooking && !showWelcome) {
        setShowWelcome(true);
      }
    }
  }, [rrLocation.pathname, rrLocation.search, rrLocation.state, navigate, showBooking, showWelcome]);

  // ---------- Efekti: pelkkä scroll-to ----------
  useEffect(() => {
    if (rrLocation.state?.scrollTo !== RESULTS_ID || scrolledOnce) return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(RESULTS_ID);
      if (el || tries > 40) {
        clearInterval(timer);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setScrolledOnce(true);
        navigate(rrLocation.pathname + rrLocation.search + rrLocation.hash, {
          replace: true,
          state: {},
        });
      }
      tries += 1;
    }, 50);
    return () => clearInterval(timer);
  }, [
    rrLocation.pathname,
    rrLocation.search,
    rrLocation.hash,
    rrLocation.state,
    navigate,
    scrolledOnce,
  ]);

  // Genret (DB → dedupe case-insensitiivisesti + aakkosjärjestys)
// Genret (DB → normalisointi + aakkosjärjestys)
useEffect(() => {
  const ac = new AbortController();
  (async () => {
    try {
      const raw = await api.genres({ signal: ac.signal });
      setAvailableGenres(normalizeGenreNames(raw));
    } catch (e) {
      if (e.name !== "AbortError") console.error("Genrejen haku epäonnistui:", e);
    } finally {
      setGenresReady(true);
    }
  })();
  return () => ac.abort();
}, []);

// ---------- API: meta (artist types) ----------
useEffect(() => {
  const ac = new AbortController();
  (async () => {
    try {
      const types = await api.artistTypes({ signal: ac.signal });
      const filtered = [...new Set((types || []).filter(Boolean).map(String))]
        .filter(t => SHOW_COMPANIES || t.toLowerCase() !== "yritys")
        .sort((a, b) => a.localeCompare(b, "fi", { sensitivity: "base" }));
      setAvailableArtistTypes(filtered); // <-- setter käytössä
      } catch {/* no-op */ }
    finally {
      setTypesReady(true);
    }
  })();
  return () => ac.abort();
}, []);

// ---------- API haut (lista) ----------
useEffect(() => {
  const ac = new AbortController();

  (async () => {
    try {
     // Esiintyjät: aina vain ne, joilla valittu päivä on auki
      const artistsPromise = api.freeBands(selectedDate || todayLocalISO(), { signal: ac.signal });

      // Yritykset (jos käytössä): samoin vain ne, joilla valittu päivä on auki
      const companiesPromise = SHOW_COMPANIES
        ? api.freeCompanies(selectedDate || todayLocalISO(), { signal: ac.signal })
        : Promise.resolve([]);

      const [artistsRes, companiesRes] = await Promise.allSettled([
        artistsPromise,
        companiesPromise,
      ]);

      const artistsRaw =
        artistsRes.status === "fulfilled" ? artistsRes.value : [];
      const companiesRaw =
        companiesRes.status === "fulfilled" ? companiesRes.value : [];

      // normalisoinnit (käyttävät normalizeArtist / normalizeCompany)
      const artists = (Array.isArray(artistsRaw) ? artistsRaw : artistsRaw?.bands ?? [])
        .map(normalizeArtist);

      const companies = SHOW_COMPANIES
        ? (Array.isArray(companiesRaw) ? companiesRaw : companiesRaw?.companies ?? [])
            .map(normalizeCompany)
        : [];

      // 1) Yhdistä
      const combined = SHOW_COMPANIES ? [...companies, ...artists] : [...artists];

      // 2) Pudota vain selvästi kiinni olevat (jos bändiltä tulee suoraan tieto, että päivä on kiinni)
      const finalList = combined.filter((b) => {
        const flag = isOpenOnDay(b, selectedDate); // true | false | null
        return flag !== false; // pidä true ja null (null = ei tietoa, mutta freeBands on jo suodattanut vapaiksi)
      });

      // 3) RANDOMISOI järjestys kerran per haku (Fisher–Yates)
      const shuffled = [...finalList];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      setBands(shuffled);

      // Päivitä palvelut (vain jos yritykset mukana)
      if (SHOW_COMPANIES) {
        const servicesFlat = companies.flatMap((c) =>
          Array.isArray(c.services)
            ? c.services
            : typeof c.services === "string"
            ? c.services.split(",")
            : []
        );
        const uniqServices = [...new Set(servicesFlat.map((s) => String(s).trim()).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, "fi", { sensitivity: "base" }));
        setAvailableServices(uniqServices);
      } else {
        setAvailableServices([]);
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error("Virhe listauksessa:", e);
      }
      } finally {
        setListReady(true);
      }
  })();

  return () => ac.abort();
}, [selectedDate]);


  // Alueet
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const raw = await api.regions({ signal: ac.signal });
        const names = (Array.isArray(raw) ? raw : [])
          .map((r) => (typeof r === "string" ? r : r?.name || r?.region || ""))
          .filter(Boolean);
        const uniqueSorted = [...new Set(names)].sort((a, b) =>
          a.localeCompare(b, "fi")
        );
        setAvailableRegions(uniqueSorted);
      } catch (e) {
        if (e.name !== "AbortError")
          console.error("Alueiden haku epäonnistui:", e);
        } finally {
        setRegionsReady(true);
      }
    })();
    return () => ac.abort();
  }, []);

  // ---------- Suodatukset ----------
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const debouncedLocation = useDebouncedValue(locationFilter, 250);
  const debouncedRegions = useDebouncedValue(selectedRegions, 120); // vain labelia varten

  const filteredBands = useMemo(() => {
  const qName = lower(debouncedSearchTerm || "");
  const qLoc  = lower(debouncedLocation || "");
  const selRegions = selectedRegions.map((r) => lower(r));
  const selTypes   = selectedArtistTypes.map((t) => lower(t));

    // jos tagit disabloitu (Kaikki), ei rajata niillä
 const selTags = tagsDisabled
   ? []
   : (isCompanyMode ? selectedServices : selectedGenres).map(lower);


    return bands
      .filter((b) => (SHOW_COMPANIES ? true : b.__kind !== "company")) // varmistus
      .filter((band) => {
        // Haku nimellä: katetaan yleisimmät nimikentät ja puretaan slug (-/_ -> välilyönti)
        const nameText = lower([
          band?.name,
          band?.band_name,
          band?.title,
          band?.display_name,
          band?.slug?.replace(/[-_]+/g, " ")
        ]);
        // Sijainti voi olla eri avaimella eri lähteissä
        const locText  = lower([band?.location, band?.hometown, band?.base_location]);
        const typeText = lower(
          band?.artist_type ||
          band?.artist_type_name ||
          band?.type ||
          ""
        );

        const bandRegions = extractRegionNames(band).map(lower);
        const bandTags =
          band.__kind === "company"
 ? (Array.isArray(band.services) ? band.services : []).map(lower)
     : extractGenres(band).map(lower);

 const matchName = !qName || nameText.includes(qName);
 const matchLocation = !qLoc || locText.includes(qLoc);
        const matchRegion =
          selRegions.length === 0 ||
          selRegions.some((r) => bandRegions.includes(r));
const matchType =
  selTypes.length === 0 ||
  selTypes.some((t) => typeText.includes(t));
        const matchTags =
          selTags.length === 0 || selTags.some((tg) => bandTags.includes(tg));

        return matchName && matchLocation && matchRegion && matchType && matchTags;
      });
  }, [
    bands,
    debouncedSearchTerm,
    debouncedLocation,
    selectedRegions,
    selectedArtistTypes,
    selectedGenres,
    selectedServices,
    isCompanyMode,
    tagsDisabled,
  ]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filteredBands]);

  const activeFiltersCount = useMemo(
    () =>
      (debouncedSearchTerm ? 1 : 0) +
      (debouncedLocation ? 1 : 0) +
      selectedRegions.length +
      selectedArtistTypes.length +
      (tagsDisabled
        ? 0
        : isCompanyMode
        ? selectedServices.length
        : selectedGenres.length),
    [
      debouncedSearchTerm,
      debouncedLocation,
      selectedRegions.length,
      selectedArtistTypes.length,
      isCompanyMode,
      selectedServices.length,
      selectedGenres.length,
      tagsDisabled,
    ]
  );

  // ---------- UI osia ----------
  const labelBase =
    "block font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs uppercase tracking-wide text-black/80 dark:text-white/80 mb-1 dark:text-shadow-xxl";
  const inputBase =
    "w-full h-10 rounded-lg px-3 font-customFont1 bg-[#111] text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-primary dark:text-shadow-xxl text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs";

  const normalizeList = (arr) =>
    Array.from(new Set(arr.map((s) => String(s).trim())));
  const artistTypesUI = useMemo(() => {
    const merged = normalizeList([...baseArtistTypes, ...(availableArtistTypes || [])]);
    return SHOW_COMPANIES ? merged : merged.filter((t) => t.toLowerCase() !== "yritys");
  }, [availableArtistTypes]);

// Enää EI yhdistetä mihinkään kovakoodattuun – vain DB
const genresUI = useMemo(() => availableGenres, [availableGenres]);

  const regionsUI = useMemo(() => availableRegions, [availableRegions]);

  const clearFilters = () => {
    setSearchTerm("");
    setLocationFilter("");
    setSelectedArtistTypes([]);
    setSelectedGenres([]);
    setSelectedServices([]);
    setSelectedRegions([]);
    setShowMoreFilters(false);
    // live-haussa ei enää hasSearched-tilaa
    // (halutessa voi scrollata tuloksiin):
    // document.getElementById(RESULTS_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Laske määrät tyypeittäin (yrityksiä ei huomioida kun SHOW_COMPANIES=false)
const countByType = (items) =>
  items.reduce(
    (acc, it) => {
      if (SHOW_COMPANIES && it.__kind === "company") {
        acc.company++;
        return acc;
      }
      const t = String(it.artist_type || "").toLowerCase();
      if (t.includes("dj")) acc.dj++;
      else if (t.includes("bilebänd") || t.includes("bileband")) acc.bilebandi++;
      else if (t.includes("trub")) acc.trubaduuri++;
      else if (t.includes("duo")) acc.duo = (acc.duo || 0) + 1;
      else acc.other++;
      return acc;
    },
    { company: 0, dj: 0, bilebandi: 0, trubaduuri: 0, duo: 0, other: 0 }
  );

  // apurit suomen taivutukseen
  const formatCountFi = (n, key) => {
    // [nominatiivi_yks, partitiivi_yks]
    const forms = {
      toimija: ["toimija", "toimijaa"],
      esiintyjä: ["esiintyjä", "esiintyjää"],
      yritys: ["yritys", "yritystä"],
    };
    const [sg, part] = forms[key] ?? [key, `${key}a`];
    return n === 1 ? `1 ${sg}` : `${n} ${part}`;
  };

const resultsLabel = useMemo(() => {
  const list = filteredBands;
  const total = list.length;

  const regionText = debouncedRegions.length
    ? debouncedRegions.join(", ")
    : "koko Suomi";

  const dateText = selectedDate
    ? formatDateFi(selectedDate)
    : formatDateFi(todayLocalISO());

  if (total === 0) {
    return `Ei ${SHOW_COMPANIES ? "toimijoita" : "esiintyjiä"} — ${regionText}, ${dateText}.`;
  }

  if (!SHOW_COMPANIES) {
    return `Löytyi ${formatCountFi(total, "esiintyjä")} — vapaina ${dateText} — ${regionText}.`;
  }

  const c = countByType(list);
  const performers =
    (c?.bilebandi || 0) +
    (c?.trubaduuri || 0) +
    (c?.dj || 0) +
    (c?.duo || 0) +
    (c?.other || 0);
  const companies = c?.company || 0;

  const pieces = [];
  if (performers) pieces.push(`${performers} esiintyjää`);
  if (companies) pieces.push(`${companies} yritystä`);

  return `Löytyi yhteensä ${formatCountFi(total, "toimija")} — ${pieces.join(
    " ja "
  )} — ${regionText}, ${dateText}.`;
}, [filteredBands, debouncedRegions, selectedDate]);

  const filtersSection = (
    <section className="space-y-3" data-aos="fade-in">
{/* PIKA-HAKU (1 rivi) */}
<div className="grid grid-cols-1 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-2 xsm:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 mdd:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 1xl:grid-cols-3 2xl:grid-cols-3 gap-3">

  {/* Milloin */}
  <label className="text-left w-full">
    <span className={labelBase}>Milloin?</span>
    <input
      type="date"
      value={selectedDate}
      onChange={(e) => setSelectedDate(e.target.value)}
      min={todayLocalISO()}
      className={`${inputBase} h-9 px-2 text-sm date--dark`}
    />
  </label>

  {/* Tyyppi */}
  <label className="text-left w-full">
    <span className={labelBase}>Tyyppi</span>
    <select
      value={selectedArtistTypes[0] ?? ""}
      onChange={(e) => {
        const v = e.target.value;
        setSelectedArtistTypes(v ? [v] : []);
        setSelectedGenres([]);
        setSelectedServices([]);
      }}
      className={`${inputBase} h-9 px-2 text-sm`}
    >
      <option value="">Kaikki</option>
      {artistTypesUI.map((t) => (
        <option key={t} value={t}>
          {t}
        </option>
      ))}
    </select>
  </label>

  {/* Missä (alue) */}
  <label className="text-left w-full s24:col-span-2 xsm:col-span-2 sm:col-span-1">
    <span className={labelBase}>Missä?</span>
    <select
      value=""
      onChange={(e) => {
        const v = e.target.value;
        if (v && !selectedRegions.includes(v)) {
          setSelectedRegions([...selectedRegions, v]);
        }
      }}
      className={`${inputBase} h-9 px-2 text-sm`}
    >
      <option value="">
        {selectedRegions.length ? `${selectedRegions.length} valittu` : "Kaikki"}
      </option>
      {regionsUI.map((r) => (
        <option key={r} value={r} disabled={selectedRegions.includes(r)}>
          {r}
        </option>
      ))}
    </select>

    {/* pikavalinnat & valitut kuten ennen */}
    {QUICK_REGIONS.filter((r) => regionsUI.includes(r)).length > 0 && (
      <div className="mt-1 flex flex-wrap gap-1">
        {QUICK_REGIONS.filter((r) => regionsUI.includes(r)).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => {
              if (!selectedRegions.includes(r)) {
                setSelectedRegions([...selectedRegions, r]);
              }
            }}
            className="text-[10px] px-2 mt-1 py-[2px] rounded-full border border-white/10 bg-white/50 hover:bg-white/80 dark:bg-electric/30 dark:text-white/90 text-black/90 dark:hover:bg-electric/80"
            title={`Lisää alue ${r}`}
          >
            {r}
          </button>
        ))}
      </div>
    )}

    {selectedRegions.length > 0 && (
      <div className="mt-1 flex flex-wrap gap-1">
        {selectedRegions.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() =>
              setSelectedRegions(selectedRegions.filter((x) => x !== r))
            }
            className="bg-electric text-black dark:bg-primary border border-white/10 rounded-full px-2 mt-1 py-[2px] text-[10px] font-semibold dark:hover:bg-teal-100 hover:bg-pink/80 dark:text-black"
            title="Poista"
            aria-label={`Poista alue ${r}`}
          >
            {r} ✕
          </button>
        ))}
      </div>
    )}
  </label>
</div>


      {/* Toggle: Lisäsuodattimet */}
      <div className="flex justify-center w-full pt-2 xxxsm:hidden xxsm:hidden s24:hidden xsm:hidden md:block">
        <button
          type="button"
          onClick={() => setShowMoreFilters((v) => !v)}
          aria-expanded={showMoreFilters}
          className="inline-flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm px-3 py-1.5 rounded-lg bg-black/20 hover:bg-black/30 dark:bg-white/10 dark:hover:bg-white/20 transition"
        >
          {showMoreFilters ? <MinusCircle size={16} /> : <PlusCircle size={16} />}
          {showMoreFilters ? "Piilota lisäsuodattimet" : "Näytä lisäsuodattimet"}
        </button>
      </div>

      {/* LISÄSUODATTIMET (2. rivi) */}
      {showMoreFilters && (
        <div className="grid grid-cols-1 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-2 xsm:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 mdd:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 1xl:grid-cols-3 2xl:grid-cols-3 gap-3">
          {/* 🎵 Musiikkityyli EKAKSI lisäsuodattimiin */}
          {!isCompanyMode && (
            <label
              className={`text-left w-full ${
                tagsDisabled ? "opacity-40 pointer-events-none select-none" : ""
              }`}
            >
              <span className={labelBase}>Musiikkityyli</span>
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && !selectedGenres.includes(v)) {
                    setSelectedGenres([...selectedGenres, v]);
                  }
                }}
                disabled={tagsDisabled}
                className={`${inputBase} h-9 px-2 text-sm`}
              >
                <option value="">
                  {selectedGenres.length ? `${selectedGenres.length} valittu` : "Kaikki"}
                </option>
                {genresUI.map((g) => (
                  <option key={g} value={g} disabled={selectedGenres.includes(g)}>
                    {g}
                  </option>
                ))}
              </select>

              {/* Pikavalinnat genreille */}
              {QUICK_GENRES.filter((g) => genresUI.includes(g)).length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {QUICK_GENRES.filter((g) => genresUI.includes(g)).map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => {
                        if (!selectedGenres.includes(g)) {
                          setSelectedGenres([...selectedGenres, g]);
                        }
                      }}
                      className="text-[10px] px-2 mt-1 py-[2px] rounded-full border border-white/10 bg-white/50 hover:bg-white/80 dark:bg-electric/30 dark:text-white/90 text-black/90 dark:hover:bg-electric/80"
                    >
                      {g}
                    </button>
                  ))}
                </div>
              )}

              {/* Valitut genret */}
              {selectedGenres.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {selectedGenres.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() =>
                        setSelectedGenres(selectedGenres.filter((x) => x !== g))
                      }
                      className="bg-electric text-black dark:bg-primary border border-white/10 rounded-full px-2 mt-1 py-[2px] text-[10px] font-semibold dark:hover:bg-teal-100 hover:bg-pink/80 dark:text-black"
                    >
                      {g} ✕
                    </button>
                  ))}
                </div>
              )}
            </label>
          )}

          {/* Kotipaikka */}
          <label className="text-left w-full">
            <span className={labelBase}>Kotipaikka</span>
            <input
              type="text"
              placeholder="Etsi…"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className={`${inputBase} h-9 px-2 text-sm`}
            />
          </label>

          {/* Etsi nimellä */}
          <label className="text-left w-full">
            <span className={labelBase}>Etsi nimellä</span>
            <input
              type="text"
              placeholder="Etsi…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${inputBase} h-9 px-2 text-sm`}
            />
          </label>
        </div>
      )}


    </section>
  );

  const resultsBar = (
    <div className="w-full text-center my-6" data-aos="fade-in">
      <p className="mt-2 text-2xl sm:text-lg text-black/70 dark:text-white/80">
        {resultsLabel}
      </p>

      {activeFiltersCount > 0 && (
        <NeonButton
          onClick={clearFilters}
          variant="primary"
          size="md"
          className="px-4 py-2 mt-4"
        >
          <span className="flex items-center gap-2">
            <span aria-hidden>×</span>
            <span>Nollaa haku</span>
            <span className="ml-2 inline-flex items-center justify-center font-semibold px-2 py-0.5 rounded-full bg-black/20">
              {activeFiltersCount}
            </span>
          </span>
        </NeonButton>
      )}
    </div>
  );

    const cardsSection = (
      <div>
        {/* voit poistaa data-aos -attribuutin, jos se häiritsee */}
        {filteredBands.length === 0 ? (
          <p className="font-customFont1 dark:text-shadow-lg text-center text-black/80 dark:text-primary text-xl font-bold">
            Ei tuloksia näillä hakuehdoilla.
          </p>
        ) : (
          <>
            <motion.div
              className="grid grid-cols-2 xxxsm:grid-cols-1 xxsm:grid-cols-1 s24:grid-cols-1 xsm:grid-cols-1 sm:grid-cols-2 mdd:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4 w-full mt-5"
              variants={listVariants}
              initial="hidden"
              animate="show"
              layout
            >
              <AnimatePresence initial={false}>
                {filteredBands.slice(0, visibleCount).map((item) => {
                  const key = `${item.__kind}-${item.id}`;
                  const openProfile = () => {
                    const d = selectedDate || new Date().toISOString().split("T")[0];
                    localStorage.setItem(DATE_KEY, d);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    if (SHOW_COMPANIES && item.__kind === "company") {
                      navigate(`/yritys/${item.slug}?date=${d}`);
                    } else {
                      navigate(`/esiintyja/${item.slug}?date=${d}`);
                    }
                  };

                  return (
                    <motion.div key={key} variants={itemVariants} layout exit="exit">
                      {item.__kind === "company" && SHOW_COMPANIES ? (
                        <CompanyCard company={item} onViewProfile={openProfile} />
                      ) : (
                        <ArtistCard band={item} onViewProfile={openProfile} />
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>

            {/* Näytä lisää / vähemmän -napit */}
            <div className="mt-6 flex justify-center gap-3">
              {visibleCount < filteredBands.length && (
                <NeonButton
                  onClick={() =>
                    setVisibleCount((c) => Math.min(c + PAGE_SIZE, filteredBands.length))
                  }
                  variant="primary"
                  size="md"
                >
                  Näytä lisää ({filteredBands.length - visibleCount})
                </NeonButton>
              )}

              {visibleCount > PAGE_SIZE && (
                <NeonButton
                  onClick={() => setVisibleCount(PAGE_SIZE)}
                  variant="secondary"
                  size="md"
                >
                  Näytä vähemmän
                </NeonButton>
              )}
            </div>
          </>
        )}
      </div>
    );

const handleApplyFromModal = ({ date, types, genres, services, regions, _preview }) => {
  // Päivitä filtterit aina
  if (date) setSelectedDate(date);
  setSelectedArtistTypes(
    SHOW_COMPANIES ? (types || []) : (types || []).filter((t) => t.toLowerCase() !== "yritys")
  );
  setSelectedGenres(genres || []);
  setSelectedServices(SHOW_COMPANIES ? (services || []) : []); // ei käytössä kun yritykset pois
  setSelectedRegions(regions || []);

  // Live-esikatselu: älä sulje modalia, älä navigoi
  if (_preview) return;

  // Vasta kun käyttäjä klikkaa CTA:n → sulje ja scrollaa tuloksiin
  setShowBooking(false);
  navigate(
    {
      pathname: "/",
      search: date ? `?date=${date}` : "",
      hash: `#${RESULTS_ID}`,
    },
    { state: { scrollTo: RESULTS_ID } }
  );
};


  // 3) Päivitetty heroTitle (fade vain esiintyjä-versioon)
const heroTitle = SHOW_COMPANIES
  ? (
      <>
        Löydä vapaat <strong>toimijat</strong> – esiintyjät ja tapahtumapalvelut – hetkessä.
        Reaaliaikainen saatavuus ja selkeä kokonaishinta.
      </>
    )
  : (
      <>
        <AnimatedLine>Löydä ja varaa esiintyjä tapahtumaasi – helposti ja nopeasti verkossa!
</AnimatedLine>
        <AnimatedLine delay={0.20}>Trubaduurit, bilebändit, duot ja DJ:t - kaikki samasta paikasta!</AnimatedLine>
      </>
    );


  return (
    <>
        {/* 🔄 Boot-overlay etusivun lataukseen
          - Taustakuva: vaihda import heroBg → omaan
          - Fade-ajat: fadeInMs / fadeOutMs
          - Logon koko/nopeus: markSize / logoSpinSec
      */}
      {bootLoading && (
        <PageOverlayLoading
          label="Ladataan sisältöä…"
          bgImage={heroBg}
          fadeInMs={0}
          fadeOutMs={600}
          logoLight={logoLight}
          logoDark={logoDark}
        />

      )}
      {/* META */}
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
        {/* OG / Twitter */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Buukkeri" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:locale" content="fi_FI" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content="https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg" />
        {/*
          Jos haluat pakottaa OG-kuvan myös clientissä, poista kommentit:
          <meta property="og:image" content="https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg" />*/}

           <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Buukkeri",
            "url": "https://buukkeri.fi/",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://buukkeri.fi/?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Buukkeri",
            "url": "https://buukkeri.fi/",
            "logo": "https://buukkeri.fi/wp-content/uploads/2025/10/og-default-1200x630-1.jpg"
          })}
        </script>

        
      </Helmet>

      {/* HERO */}
      <WebDesignBanner>
        <div className="text-center">
          <h1 className="mb-8 dark:text-white/80 text-white/80 font-customFont1 text-xs md:text-lg font-semibold text-center text-shadow-lg">
            {heroTitle}
          </h1>
        </div>

        <div className="relative z-30 overflow-visible mx-auto w-full max-w-6xl rounded-2xl p-6 bg-lemon/60 dark:bg-black/40 backdrop-blur-md shadow-lg ring-1 ring-white/40">
          {filtersSection}
        </div>
      </WebDesignBanner>

      {/* SIVUN SISÄLTÖ */}
      <div className="relative min-h-screen w-full pb-28 sm:pb-10">
        <div className="relative z-10 max-w-7xl mx-auto sm:px-4 md:px-4 mdd:px-4 lg:px-4 xl:px-4 2xl:px-4">
          <div className="grid grid-cols-1 gap-4 ">
            <div className="order-1 lg:col-span-3" />
            {/* Kortit laatikossa */}
            <div className="order-3 lg:order-2 lg:col-span-3 scroll-mt-[96px]" id={RESULTS_ID}>
              <div className="rounded-2xl p-6 bg-lemon/60 dark:bg-black/40 backdrop-blur-md shadow-lg mt-10">
                 {resultsBar}
                 {cardsSection}
              </div>

              <div className="h-10" />
              <div className="rounded-2xl md:p-6 bg-lemon/100 dark:bg-black/60 backdrop-blur-md shadow-lg">
                <BookingFlow />
                <LatestArtists title="Buukkerin uusimmat esiintyjät" />
                <MonthlyFeatured />
                <WhyBuukkeriSection />
              </div>
            </div>
          </div>
        </div>
      </div>

      {SHOW_WELCOME && (
        <WelcomeModal
          open={showWelcome}
          onClose={() => {
            setShowWelcome(false);
            try { sessionStorage.setItem(SEEN_KEY, "1"); } catch {}
          }}
          onStart={() => {
            try { sessionStorage.setItem(SEEN_KEY, "1"); } catch {}
            setShowWelcome(false);
            // setShowBooking(true); // avaa pikahaku, kommentoituna pois päältä
          }}
        />
      )}

      {/* Booking modal (pikahaku) */}

      <BookingModal
        open={showBooking}
        onClose={closeBooking}
        onApply={handleApplyFromModal}
        artistTypes={artistTypesUI}
        genres={genresUI}
        services={SHOW_COMPANIES ? availableServices : []} // ei palveluita kun yritykset pois
        initialDate={selectedDate || todayLocalISO()}
        isWelcome={false}
        regions={regionsUI}
      />
    </>
  );
}
